import { z } from 'zod';
import { coreIdSchema } from './ids.js';
import {
  OrganizationSchema,
  OrganizationSubscriptionSchema,
  OrganizationSubscriptionTierSchema,
  OrgOnboardingProfileSchema,
} from './organization.types.js';

/**
 * The wire `error` code a company-scoped route answers with when the active
 * organization is not attached to a billing entity.
 *
 * Like the entitlement refusal, this is an answer and not an outage: no
 * retry attaches a company. Clients branch on it to say so instead of
 * offering a Retry that re-issues the same refusal forever, which makes it a
 * contract rather than a message — hence one spelling, here.
 */
export const BILLING_ENTITY_REQUIRED_CODE = 'billing_entity_required';

/**
 * A billing entity is the commercial owner of one or more organizations — the
 * company Reflex contracts with. A customer commonly splits into several
 * organizations (finance, engineering) that share one identity provider and
 * one commercial relationship, while day-to-day work stays strictly inside
 * one organization: membership, RBAC, and every org-scoped table keep their
 * single `organization_id` fence, and nothing about a billing entity grants
 * cross-org data access.
 *
 * What the billing entity carries is what is genuinely billing entity-level:
 *
 * - The subscription tier. A billing entity's tier governs every organization it
 *   owns (see `effectiveSubscriptionTier`), so one Enterprise contract
 *   entitles all of the customer's orgs without per-org bookkeeping.
 * - Future identity surfaces — the product design places IdP configuration
 *   and verified domain claims at the billing entity level, with each org choosing
 *   how it consumes them.
 *
 * Every organization belongs to exactly one billing entity — but not yet at
 * the storage layer: `organizations.billing_entity_id` is deliberately still
 * nullable for the bake period in migration `0109`, because the previous
 * image's org create does not stamp it. Until the cleanup migration sets it
 * NOT NULL, company-scoped routes can meet an org with no company and answer
 * {@link BILLING_ENTITY_REQUIRED_CODE}.
 */
export const BillingEntitySchema = z
  .object({
    /**
     * Prefix-checked so a company id stays distinguishable from an
     * organization id on the wire, not just inside the server.
     */
    id: coreIdSchema('billingEntity'),
    slug: z.string(),
    name: z.string(),
    /**
     * The billing entity's subscription tier. Authoritative for every organization
     * the billing entity owns. See `SUBSCRIPTION_TIER_FEATURES` for what a
     * tier entitles.
     */
    subscriptionTier: OrganizationSubscriptionTierSchema,
    createdByUserId: z.string().nullable(),
    createdAt: z.number(),
  })
  // Tagged so the OpenAPI generator emits one reusable `BillingEntity` component.
  .meta({ id: 'BillingEntity' });
export type BillingEntity = z.infer<typeof BillingEntitySchema>;

/** Platform-admin request to create a billing entity. */
export const CreateBillingEntityRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: z.string().trim().min(1).max(100).optional(),
  subscriptionTier: OrganizationSubscriptionTierSchema.default('standard'),
});
export type CreateBillingEntityRequest = z.infer<typeof CreateBillingEntityRequestSchema>;

/** Platform-admin request to rename a billing entity or change its tier. */
export const UpdateBillingEntityRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  slug: z.string().trim().min(1).max(100).optional(),
  subscriptionTier: OrganizationSubscriptionTierSchema.optional(),
});
export type UpdateBillingEntityRequest = z.infer<typeof UpdateBillingEntityRequestSchema>;

/**
 * Platform-admin request to move an organization to another billing entity.
 *
 * A move re-homes the organization's subscription window and can switch its
 * single sign-on off, so it carries a written `reason` for the audit row. When
 * the organization requires single sign-on through its current company and
 * the new company trusts a different identity provider, the move is refused
 * (409 `sso_reset_acknowledgement_required`) until `acknowledgeSsoReset` is
 * sent, so an operator cannot lock an organization out by accident.
 */
export const SetOrganizationBillingEntityRequestSchema = z.object({
  billingEntityId: coreIdSchema('billingEntity'),
  reason: z.string().trim().min(1).max(500),
  acknowledgeSsoReset: z.boolean().optional(),
});
export type SetOrganizationBillingEntityRequest = z.infer<
  typeof SetOrganizationBillingEntityRequestSchema
>;

/** The per-organization SSO opt-in flags a company move can switch off. */
export const OrganizationSsoFlagSchema = z.enum(['ssoEnabled', 'requireSso', 'jitEnabled']);
export type OrganizationSsoFlag = z.infer<typeof OrganizationSsoFlagSchema>;

/**
 * What a company move changed, so the operator sees the consequences rather
 * than inferring them: the subscription window before and after (a
 * trial-to-unlimited or unlimited-to-expired change is the difference), and
 * any SSO flags the move switched off.
 */
export const AdminOrganizationBillingEntityMoveResultSchema = z
  .object({
    organization: OrganizationSchema,
    previousBillingEntityId: z.string().nullable(),
    changed: z.boolean(),
    subscriptionBefore: OrganizationSubscriptionSchema.nullable(),
    subscriptionAfter: OrganizationSubscriptionSchema.nullable(),
    clearedSsoFlags: z.array(OrganizationSsoFlagSchema),
  })
  .meta({ id: 'AdminOrganizationBillingEntityMoveResult' });
export type AdminOrganizationBillingEntityMoveResult = z.infer<
  typeof AdminOrganizationBillingEntityMoveResultSchema
>;

export const AdminListBillingEntitiesResponseSchema = z.object({
  billingEntities: z.array(BillingEntitySchema),
});
export type AdminListBillingEntitiesResponse = z.infer<
  typeof AdminListBillingEntitiesResponseSchema
>;

/** Enough of an owned organization to name it in the admin surfaces. */
export const BillingEntityOwnedOrganizationSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
});
export type BillingEntityOwnedOrganization = z.infer<typeof BillingEntityOwnedOrganizationSchema>;

/**
 * One billing entity with the organizations it owns — enough for the admin surfaces
 * to show the blast radius of a billing entity-level change without shipping full
 * organization views.
 */
export const AdminBillingEntityDetailSchema = z
  .object({
    billingEntity: BillingEntitySchema,
    organizations: z.array(BillingEntityOwnedOrganizationSchema),
  })
  .meta({ id: 'AdminBillingEntityDetail' });
export type AdminBillingEntityDetail = z.infer<typeof AdminBillingEntityDetailSchema>;

/**
 * The company as its own customers see it, from inside one of the
 * organizations it owns. Read by `GET /company` and rendered by the Company
 * pages, gated on `company:read` in the active organization.
 *
 * What it deliberately omits is the point: no company id and no slug. A
 * customer request never names the company it acts on — the active
 * organization is the door, and the server takes the owner from the caller's
 * membership (`request.currentBillingEntityId`). Shipping the id would invite
 * a client to send one back, which is a platform-admin act.
 *
 * `organizations` is the blast radius of a company-level change — the
 * siblings a new identity provider or a tier change would also cover — so a
 * Company Manager can see what they are about to affect. That is why the read
 * sits behind `company:read`, which is not in the derived read set an ordinary
 * viewer holds.
 */
export const CompanySchema = z
  .object({
    name: z.string(),
    /** Authoritative for every organization the company owns. */
    subscriptionTier: OrganizationSubscriptionTierSchema,
    organizations: z.array(BillingEntityOwnedOrganizationSchema),
  })
  .meta({ id: 'Company' });
export type Company = z.infer<typeof CompanySchema>;

/** The written reason every platform-admin domain mutation records in the audit trail. */
export const AdminCompanyEmailDomainReasonSchema = z.string().trim().min(1).max(500);

export const AdminRemoveCompanyEmailDomainRequestSchema = z
  .object({ reason: AdminCompanyEmailDomainReasonSchema })
  .strict();
export type AdminRemoveCompanyEmailDomainRequest = z.infer<
  typeof AdminRemoveCompanyEmailDomainRequestSchema
>;

export const AdminReassignCompanyEmailDomainRequestSchema = z
  .object({
    billingEntityId: coreIdSchema('billingEntity'),
    reason: AdminCompanyEmailDomainReasonSchema,
  })
  .strict();
export type AdminReassignCompanyEmailDomainRequest = z.infer<
  typeof AdminReassignCompanyEmailDomainRequestSchema
>;

/**
 * Add an organization to the company that owns the active organization —
 * `POST /company/organizations`, written by Configure → Company → Overview.
 *
 * It names no company, for the same reason {@link CompanySchema} returns
 * none: the active organization is the door and the server takes the owner
 * from the caller's membership. It also carries no company fields at all —
 * this request joins an existing company rather than minting one, which is
 * what separates it from `CreateOrganizationRequestSchema` (whose optional
 * `companyName`/`companySlug` always create a second company). The new
 * organization inherits its owner's plan, so there is no tier to send
 * either.
 */
export const CreateCompanyOrganizationRequestSchema = z
  .object({
    name: z.string().min(1).max(100),
    /**
     * Optional URL slug. Omitted, the server derives one under the company's
     * slug (`acme-finance`) so sibling organizations read as a family, and
     * de-duplicates it.
     */
    slug: z.string().min(1).max(60).optional(),
    /** See `CreateOrganizationRequestSchema.supportEmail`. */
    supportEmail: z.string().email().optional(),
    /**
     * Self-described onboarding profile; drives the starting plugin set the
     * same way it does on the self-serve create form. See
     * `OrgOnboardingProfileSchema`.
     */
    orgOnboardingProfile: OrgOnboardingProfileSchema.optional(),
  })
  .meta({ id: 'CreateCompanyOrganizationRequest' });
export type CreateCompanyOrganizationRequest = z.infer<
  typeof CreateCompanyOrganizationRequestSchema
>;

/**
 * The commercial owner of one organization, read from the platform-admin
 * surface. `organizations` lists every org the billing entity owns (the blast
 * radius of a company-level change).
 *
 * This is the only read that reports an organization's billing entity. Commercial
 * ownership deliberately does not ride the organization resource: billing entities
 * are an internal, platform-admin concept, so nothing about them reaches an
 * organization's own members or the public API.
 */
export const AdminOrganizationBillingEntitySchema = z
  .object({
    billingEntity: BillingEntitySchema,
    organizations: z.array(BillingEntityOwnedOrganizationSchema),
  })
  .meta({ id: 'AdminOrganizationBillingEntity' });
export type AdminOrganizationBillingEntity = z.infer<typeof AdminOrganizationBillingEntitySchema>;
